import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { Hospital, InfoIcon, type LucideIcon, Stethoscope } from 'lucide-react-native';
import { Fragment, memo, type ReactNode } from 'react';
import { ScrollView } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { primum_default_avatar } from '@/assets/images';
import { ClinicalTrialsSection } from '@/components/profile/ClinicalTrialsSection';
import { ExpertMetrics, useHasExpertMetrics } from '@/components/profile/ExpertMetrics';
import { ProfileChips } from '@/components/profile/ProfileChips';
import { type ProfileItemPart, renderProfileItemPart } from '@/components/profile/ProfileItemPart';
import { ProfileSectionsSkeleton } from '@/components/profile/ProfileSectionsSkeleton';
import {
  ProfileBodyText,
  ProfileItemMeta,
  ProfileSectionTitle,
} from '@/components/profile/ProfileText';
import { PublicationsSection } from '@/components/profile/PublicationsSection';
import { ResponseTime } from '@/components/responseTime';
import { Avatar, AvatarFallbackText, AvatarImage } from '@/components/ui/avatar';
import { Badge, BadgeIcon, BadgeText } from '@/components/ui/badge';
import { Divider } from '@/components/ui/divider';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { SectionList } from '@/components/ui/section-list';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import { SPECIALTY_BADGE_CLASS, SPECIALTY_BADGE_TEXT_CLASS } from '@/constants/badges';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { ANONYMOUS_EXPERT } from '@/constants/users';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { useColors } from '@/hooks/useColors';
import { useExpertProfileData } from '@/hooks/useExpertProfileData';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { getAvatarUrl } from '@/util/getAvatarUrl';
import { type ExpertProfile } from '@/util/mapDirectoryProfiles';
import { profileTrialCounts, shownTrialCount } from '@/util/profileTrialCounts';
import { isAskAnonymously, RESPONSE_TIME_VARIES } from '@/util/userFilters';

/**
 * Desktop expert profile shown beside the inquiry composer.
 *
 * LEGACY DIVERGENCE: this hand-builds the header and renders the bio sections
 * from raw `bioSections` data, instead of reusing the shared `ProfessionalSection`
 * and bio section components the profile page uses. That parallel path is the
 * source of typography drift between the two surfaces. New work should move this
 * onto the shared `components/profile/` components rather than extend it here.
 * See `.claude/rules/profile.md`.
 */
interface UserProfileSidebarProps {
  expertId?: string | number;
  expert?: ExpertProfile;
  /**
   * Optional content rendered inside the profile card, directly beneath the
   * header block and above the bio sections. A generic composition slot so a
   * host surface (e.g. Fellows Corner's seed Q&A or a tab bar) can inject
   * content without this shared component taking a feature dependency.
   */
  afterHeader?: ReactNode;
  /**
   * Icons for the role and institution rows. Default to the clinical
   * `Stethoscope`/`Hospital`; a host overrides them for a non-clinician (e.g. an
   * attorney mentor) so the icons match the profession. Kept as props because
   * `identifiesAsDoctor` is unreliable here and can't drive the choice.
   */
  roleIcon?: LucideIcon;
  institutionIcon?: LucideIcon;
  /**
   * Suppresses the bio sections (and their footers), leaving only the header
   * and `afterHeader`. Lets a host build a tabbed body — e.g. an `afterHeader`
   * tab bar that swaps the bio for its own panel — without this component
   * knowing about the tabs.
   */
  hideSections?: boolean;
}

/** DOM ids used as scroll targets when a research chip is tapped (web). */
const SIDEBAR_SECTION_IDS = {
  publications: 'sidebar-publications',
  trials: 'sidebar-trials',
} as const;

/** Bio section titles, set in `util/mapBioSections.ts`. */
const ABOUT_SECTION_TITLE = 'About Me';

const EDUCATION_SECTION_TITLE = 'Education & Training';

const DISCLOSURES_SECTION_TITLE = 'Disclosures';

/**
 * Splits an education item's "degree\ninstitution" text into shared profile
 * item parts (degree as the green item title, institution as the gray meta
 * line). Work history items arrive with `accoladeParts` already built; education
 * items do not, so the sidebar builds them here at render time. Keeps the
 * sidebar's education rows consistent with the profile page's `EducationSection`
 * without reimplementing the type scale.
 */
function toEducationParts(text?: string | null): ProfileItemPart[] {
  const [degree, ...rest] = (text ?? '').split('\n');

  const institution = rest.join('\n').trim();

  const parts: ProfileItemPart[] = [];

  if (degree?.trim()) {
    parts.push({ type: 'title', text: degree.trim() });
  }

  if (institution) {
    parts.push({ type: 'meta', text: institution });
  }

  return parts;
}

/**
 * Builds a disclosure's parts: the category (e.g. "Advisory") as the green item
 * title and the entities as the gray meta line beneath it, matching the section
 * shape every other bio item follows and the profile page's `DisclosuresSection`.
 * The category fills the title slot, so a disclosure row renders no separate
 * leading meta line, unlike work history and education whose leading line is the
 * date range.
 */
function toDisclosureParts(category?: string, entities?: string | null): ProfileItemPart[] {
  const parts: ProfileItemPart[] = [];

  if (category?.trim()) {
    parts.push({ type: 'title', text: category.trim() });
  }

  if (entities?.trim()) {
    parts.push({ type: 'meta', text: entities.trim() });
  }

  return parts;
}

/**
 * Closes out the header block before the bio sections. `afterHeader` takes
 * priority when a host supplies it. Otherwise, when the metrics strip is
 * showing, its own tint and top border already separate it from the bio
 * content below, so a spacer preserves the rhythm without drawing a second,
 * redundant boundary; without the strip, a divider is the only edge marking
 * that break.
 */
function renderHeaderFooter(afterHeader: ReactNode, hasExpertMetrics: boolean): ReactNode {
  if (afterHeader) {
    return <VStack className="pb-2 pt-1">{afterHeader}</VStack>;
  }

  if (hasExpertMetrics) {
    return <View className="mb-8" />;
  }

  return <Divider className="mb-8 mt-6" />;
}

export const UserProfileSidebar = memo(
  ({
    expertId,
    expert,
    afterHeader,
    hideSections,
    roleIcon = Stethoscope,
    institutionIcon = Hospital,
  }: UserProfileSidebarProps) => {
    const { colors } = useColors();

    const {
      userData: expertData,
      isLoading: isLoadingProfile,
      name,
      cancerFocusAreas,
      subspecialtyItems,
      bioSections,
    } = useExpertProfileData(expertId, { mode: 'public-only', retryFailedOnMount: false });

    const isAnonymous = isAskAnonymously(expert?.name);

    /**
     * The caller already holds an avatar for the expert it asked us to show, so
     * the profile fetch is not what decides whether one can be drawn. Waiting on
     * it left an empty circle that filled in a beat later. Both paths resolve the
     * same stored URL, so the fetched value replaces an image already painted
     * rather than a gap.
     */
    const avatarSource =
      getAvatarUrl(expertData?.avatar, true) ??
      (expert?.avatarSrc ? { uri: expert.avatarSrc } : undefined) ??
      primum_default_avatar;

    const subspecialtyLabels = subspecialtyItems.join(', ');

    /**
     * Publications and clinical trials come from auth-only endpoints, so they
     * can't load for logged-out viewers on public profile routes. Used to hide
     * the sections rather than render a permanent error state.
     */
    const { isAuthenticated } = useAuthStatus();

    const institutionalTrialsEnabled = useFeatureFlag(FEATURE_FLAGS.INSTITUTIONAL_TRIALS);

    const showPublicationsAndTrials = isAuthenticated && !!expertData?.id;

    const researchExpertId = expertData?.id;

    const trialCounts = profileTrialCounts(expertData);

    /**
     * The research lists, in the profile page's order: publications, then clinical
     * trials. Built once because they render from one of two places depending on
     * whether this expert wrote a bio, and both places must produce the same thing.
     */
    const researchEntries: { id: string; node: ReactNode }[] = [];

    if (showPublicationsAndTrials && researchExpertId && !hideSections) {
      if ((expertData?.publicationsCount ?? 0) > 0) {
        researchEntries.push({
          id: SIDEBAR_SECTION_IDS.publications,
          node: <PublicationsSection expertId={researchExpertId} />,
        });
      }

      const institutionalShown = institutionalTrialsEnabled ? trialCounts.institutional : 0;

      if (trialCounts.own + institutionalShown > 0) {
        researchEntries.push({
          id: SIDEBAR_SECTION_IDS.trials,
          node: (
            <ClinicalTrialsSection
              expertId={researchExpertId}
              institutionalCount={institutionalShown}
              institutionName={expertData?.institutionOrganizationName}
            />
          ),
        });
      }
    }

    /**
     * Whether the research lists open the body. The profile page orders sections
     * About Me -> research -> work history -> education -> disclosures, and this
     * matches it: research sits under About Me when there is one, and ahead of
     * every bio section when there is not.
     *
     * They used to hang off whichever section happened to be first, and
     * `mapBioSections` omits About Me when the blurb is empty, so an expert
     * without a bio had work history silently promoted above their publications.
     */
    const researchLeads =
      researchEntries.length > 0 &&
      !bioSections.some((section) => section.title === ABOUT_SECTION_TITLE);

    /**
     * `leadingDivider` is false only in the leading position, where the header
     * block already closes itself off and a second boundary would double up.
     */
    const renderResearchSections = (leadingDivider: boolean) =>
      researchEntries.length ? (
        <VStack className={twJoin(leadingDivider && 'pt-2')}>
          {researchEntries.map((entry, index) => (
            <Fragment key={entry.id}>
              {leadingDivider || index > 0 ? <Divider className="my-6" /> : null}

              <View nativeID={entry.id} className="web:[scroll-margin-top:16px]">
                {entry.node}
              </View>
            </Fragment>
          ))}
        </VStack>
      ) : null;

    /**
     * Whether the metrics footer has anything to hold. Not every profile this renders carries
     * either metric — a Fellows mentor from outside the platform has no response time and no
     * badge — and the band is chrome, so without this it drew its own border, tint and padding
     * around nothing.
     */
    const hasExpertMetrics = useHasExpertMetrics({
      expertId: expertData?.id ?? '',
      responseTime: expertData?.responseTime ?? '',
    });

    /**
     * Scrolls to a section by DOM id. Uses 'auto' (instant) rather than 'smooth'
     * because the nested react-native-web scroll container does not honor smooth
     * scrolling — it would no-op.
     */
    const scrollToSidebarSection = (sectionId: string) => {
      if (!isWeb) {
        return;
      }

      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'auto', block: 'start' });
    };

    if (isAnonymous) {
      return (
        <ScrollView>
          <VStack className="rounded-lg border border-outline-100">
            <VStack className="bg-background-50 py-8">
              <VStack className="mb-4 items-center gap-x-6 px-5 lg:flex-row">
                <Avatar size="xl" className="mb-2 lg:mb-0">
                  <AvatarFallbackText>{ANONYMOUS_EXPERT.name}</AvatarFallbackText>

                  <AvatarImage source={{ uri: ANONYMOUS_EXPERT.avatarSrc }} />
                </Avatar>

                <VStack className="items-center lg:items-start lg:justify-center">
                  <Heading size="h2-2xl" className="mb-2.5 text-xl">
                    {ANONYMOUS_EXPERT.name}
                  </Heading>

                  <Text className="text-wrap text-typography-700">
                    {ANONYMOUS_EXPERT.focusAreaExpanded}
                  </Text>
                </VStack>
              </VStack>

              <VStack className="mx-4">
                <ResponseTime responseTimeLabel={RESPONSE_TIME_VARIES} fontSize="text-sm" />
              </VStack>
            </VStack>

            <Divider />

            <VStack className="items-center justify-center gap-6 px-5 py-8">
              <Text>{ANONYMOUS_EXPERT.preferredExpertMessage}</Text>

              <Text>{ANONYMOUS_EXPERT.simpleSubmissionMessage}</Text>

              <Badge action="info" size="md" className="items-start gap-2 px-4 py-3">
                <BadgeIcon as={InfoIcon} size="md" color={colors.info['600']} />

                <BadgeText className="lowercase">
                  {ANONYMOUS_EXPERT.specificProviderMessage}
                </BadgeText>
              </Badge>
            </VStack>
          </VStack>
        </ScrollView>
      );
    }

    return (
      <SectionList
        className="flex-1 rounded-lg border border-outline-100 pb-8"
        showsHorizontalScrollIndicator={false}
        /* The header draws from what the caller already holds, so only the body
           is still waiting. Empty means loaded-with-nothing once the fetch
           lands, which is a real state and keeps its own rendering. */
        ListEmptyComponent={isLoadingProfile && !hideSections ? <ProfileSectionsSkeleton /> : null}
        ListHeaderComponent={() => (
          <>
            {/*
             * White, so the metrics footer below can be the tinted one. This block used to
             * be `background-50` itself, which left nothing for a footer band to contrast
             * against: the next step down the ramp, `background-100`, separates from it at
             * 1.02:1 and is invisible. The directory card groups its metrics on a tint over
             * a white body, and this is the same arrangement rather than a new one.
             */}
            <VStack
              className="gap-x-6 bg-background-0 px-5 pb-0 pt-8"
              data-component="expert-profile-desktop">
              <HStack className="items-center gap-6">
                <Avatar size="xl" className="mb-2 lg:mb-0">
                  <AvatarImage source={avatarSource} />
                </Avatar>

                <VStack className="flex-1" space="xs">
                  <Heading size="h2-2xl" className="mb-2 text-xl">
                    {name}
                  </Heading>

                  <HStack className="flex-col items-baseline xl:flex-row">
                    {expert?.speciality ? (
                      <Badge
                        action="info"
                        size="lg"
                        className={twJoin('mb-2 xl:mb-0', SPECIALTY_BADGE_CLASS)}>
                        <BadgeText className={SPECIALTY_BADGE_TEXT_CLASS}>
                          {expert?.speciality || ''}
                        </BadgeText>
                      </Badge>
                    ) : null}
                    {expertData?.city || expertData?.state ? (
                      <Text className="mx-3 hidden text-sm text-typography-700 xl:block">•</Text>
                    ) : null}
                    <Text className="text-sm xl:block">
                      {expertData?.city ? `${expertData?.city}, ` : ''}
                      {expertData?.state || ''}
                    </Text>
                  </HStack>
                </VStack>
              </HStack>

              <Divider className="my-4" />

              <VStack className="flex-1">
                <VStack className="gap-2">
                  {expert?.institution ? (
                    <HStack className="items-start gap-2" data-component="expert-institution">
                      <Icon as={institutionIcon} className="shrink-0 text-typography-600" />

                      <Text className="text-typography-800">{expert?.institution || ''}</Text>
                    </HStack>
                  ) : null}

                  {expert?.title ? (
                    <HStack className="items-start gap-2" data-component="expert-title">
                      <Icon as={roleIcon} className="shrink-0 text-typography-600" />
                      <Text className="text-typography-800">{expert?.title || ''}</Text>
                    </HStack>
                  ) : null}

                  {cancerFocusAreas ? (
                    <HStack className="items-center" data-component="expert-focus-area">
                      <Text className="ml-7 text-typography-800">
                        Focus areas:{' '}
                        <Text className="font-medium text-typography-800">{cancerFocusAreas}</Text>
                      </Text>
                    </HStack>
                  ) : null}

                  {subspecialtyLabels ? (
                    <HStack
                      className="items-center web:mb-4"
                      data-component="expert-subspecialties">
                      <Text className="ml-7 text-typography-800">
                        Subspecialties:{' '}
                        <Text className="font-medium text-typography-800">
                          {subspecialtyLabels}
                        </Text>
                      </Text>
                    </HStack>
                  ) : null}

                  {showPublicationsAndTrials && expertData?.id ? (
                    <ProfileChips
                      expertId={expertData.id}
                      publicationsCount={expertData.publicationsCount ?? 0}
                      clinicalTrialsCount={shownTrialCount(expertData, institutionalTrialsEnabled)}
                      className="mt-1"
                      onSelect={(target) => scrollToSidebarSection(SIDEBAR_SECTION_IDS[target])}
                    />
                  ) : null}
                </VStack>
              </VStack>

              {/*
               * Metrics footer. The tint is what groups the response time and the badge into
               * a pair, which is how the directory card already reads, and it means neither
               * one needs chrome of its own to look deliberate. That is why the badge drops
               * its lifted white surface here: on a tint, a second surface is redundant.
               *
               * Its own affordance is a dotted underline on the label, so nothing here needs to
               * reserve width or height for it: the criteria opens in a popover above the card
               * rather than inside this row.
               *
               * Bled to the card edges with a negative inset, since a footer that stops short
               * of the sides reads as a box inside the card rather than as its base.
               */}
              {hasExpertMetrics ? (
                <VStack className="-mx-5 mt-6 border-t border-outline-200 bg-background-50 px-5 py-3">
                  <ExpertMetrics
                    expertId={expertData?.id ?? ''}
                    responseTime={expertData?.responseTime ?? ''}
                    size="text-sm"
                    explain
                    explainSource="inquiry_sidebar"
                  />
                </VStack>
              ) : null}
            </VStack>

            {renderHeaderFooter(afterHeader, hasExpertMetrics)}

            {researchLeads ? renderResearchSections(false) : null}
          </>
        )}
        sections={
          hideSections
            ? []
            : bioSections.map((section) => ({
                ...section,
                data: section.data ?? [],
              }))
        }
        renderSectionHeader={({ section: { title } }) => (
          <>
            {title === bioSections[0]?.title && !researchLeads ? null : (
              <Divider className="my-6" />
            )}

            <VStack className="px-5">
              <ProfileSectionTitle className="mb-3">{title}</ProfileSectionTitle>
            </VStack>
          </>
        )}
        renderSectionFooter={({ section }) =>
          section.title === ABOUT_SECTION_TITLE ? renderResearchSections(true) : null
        }
        renderItem={({ item, index, section }) => {
          const { title, text } = item;

          const isDisclosures = section.title === DISCLOSURES_SECTION_TITLE;

          let parts = (item as { accoladeParts?: ProfileItemPart[] }).accoladeParts ?? [];

          if (!parts.length && section.title === EDUCATION_SECTION_TITLE) {
            parts = toEducationParts(text);
          }

          if (!parts.length && isDisclosures) {
            parts = toDisclosureParts(title, text);
          }

          const leadingTitle = isDisclosures ? undefined : title;

          return (
            <VStack className={twJoin('gap-1 px-5', section.data.length - 1 !== index && 'mb-4')}>
              {leadingTitle ? (
                <ProfileItemMeta className="mb-1">{leadingTitle}</ProfileItemMeta>
              ) : null}

              {parts.length ? (
                parts.map((part, partIndex) => (
                  <Fragment key={partIndex}>{renderProfileItemPart({ item: part })}</Fragment>
                ))
              ) : (
                <ProfileBodyText>{text || ''}</ProfileBodyText>
              )}
            </VStack>
          );
        }}
      />
    );
  }
);
